import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import { exportToExcel } from "../utils/exportData";
import { BASE_URL } from "../api";
import {
  getApiHeaders,
  formatDate,
  shortAddress,
  getExplorerAddressUrl,
  getExplorerTxUrl,
  getExplorerName,
} from "../utils/helpers";
import axios from "axios";
import copy from "copy-to-clipboard";
import MessageToster from "../component/MessageToster";
import CommonTable from "../component/CommonTable";
import ExportButton from "../component/ExportButton";
import MobileFilter from "../component/MobileFilter";

export default function CryptoDepositHistory() {
  const [data, setData] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [appliedFromDate, setAppliedFromDate] = useState("");
  const [appliedToDate, setAppliedToDate] = useState("");

  // Details Modal state
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  // Close modal on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setSelectedDeposit(null);
      }
    };
    if (selectedDeposit) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedDeposit]);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/account/deposit_history`, {
          headers: getApiHeaders(),
        });
        if (res.data && res.data.data) {
          const raw = res.data.data;
          let records = [];
          if (Array.isArray(raw)) {
            records = Array.isArray(raw[0]) ? raw.flat(Infinity) : raw;
          } else if (raw && typeof raw === "object") {
            records = [raw];
          }
          // Filter out dummy or status-only response objects
          const validRecords = records.filter(
            (item) => item && typeof item === "object" && (item.id || item.invoiceid || item.orderno || item.to)
          );
          setData(validRecords.length > 0 ? validRecords : records);
        }
      } catch (error) {
        console.error("Error fetching deposit history:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  const handleCopy = (text, key, label = "Text") => {
    if (!text) return;
    copy(String(text).trim());
    setCopiedKey(key);
    MessageToster("success", `${label} copied to clipboard!`);
    setTimeout(() => {
      setCopiedKey(null);
    }, 2000);
  };

  const renderStatusBadge = (status) => {
    const statusStr = String(status || "").toLowerCase();
    const isPending = ["pending", "processing", "0"].includes(statusStr);
    const isSuccess = ["confirm", "success", "completed", "approved", "done", "1"].includes(statusStr);
    const isCancelled = ["cancelled", "canceled", "rejected", "failed", "expire", "expired"].includes(statusStr);

    let bgClass = "bg-secondary bg-opacity-10 text-secondary";
    let icon = "bx-help-circle";

    if (isPending) {
      bgClass = "bg-warning bg-opacity-10 text-warning";
      icon = "bx-time-five";
    } else if (isSuccess) {
      bgClass = "bg-success bg-opacity-10 text-success";
      icon = "bx-check-circle";
    } else if (isCancelled) {
      bgClass = "bg-danger bg-opacity-10 text-danger";
      icon = "bx-x-circle";
    }

    return (
      <span className={`badge fw-bold px-3 py-2 rounded-pill d-inline-flex align-items-center gap-1 ${bgClass}`}>
        <i className={`bx ${icon}`}></i>
        <span>{status || "-"}</span>
      </span>
    );
  };

  const filteredData = data.filter((item) => {
    let match = true;

    if (appliedFromDate || appliedToDate) {
      const itemDateStr = item.ondate ? item.ondate.split(" ")[0] : "";
      const itemDate = new Date(itemDateStr);
      itemDate.setHours(0, 0, 0, 0);

      if (appliedFromDate) {
        const from = new Date(appliedFromDate);
        from.setHours(0, 0, 0, 0);
        if (itemDate < from) match = false;
      }

      if (appliedToDate) {
        const to = new Date(appliedToDate);
        to.setHours(23, 59, 59, 999);
        if (itemDate > to) match = false;
      }
    }

    if (!match) return false;

    if (appliedSearchTerm) {
      if (!item || typeof item !== "object") return false;
      match = Object.values(item).some((val) =>
        String(val ?? "").toLowerCase().includes(appliedSearchTerm.toLowerCase())
      );
    }

    return match;
  });

  const handleExport = () => {
    const exportData = filteredData.map((item, index) => ({
      "#": index + 1,
      Date: formatDate(item.ondate),
      "Invoice ID": item.invoiceid || item.orderno || "-",
      Amount: item.amount != null ? item.amount : 0,
      Coin: item.coinname || "-",
      Network: item.networkname || "-",
      "Deposit Address": item.to || "-",
      "Transaction Hash": item.tranhash || "-",
      Status: item.istatus || item.status || "-",
    }));
    exportToExcel(exportData, "Crypto_Deposit_History", "default");
  };

  const handleSearch = () => {
    setAppliedSearchTerm(searchTerm);
    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFromDate("");
    setToDate("");
    setAppliedSearchTerm("");
    setAppliedFromDate("");
    setAppliedToDate("");
  };

  return (
    <div id="layout-wrapper">
      <Topbar />
      <Sidebar />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="page-title-box d-flex align-items-center justify-content-between">
                  <h2 className="fw-700 mb-0">Crypto Deposit</h2>
                  <div className="page-title-right">
                    <button
                      className="btn btn-primary waves-effect waves-light d-flex align-items-center"
                      onClick={() => navigate("/deposit")}
                    >
                      <i className="bx bx-plus me-2 fs-5"></i> Crypto Deposit
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex flex-wrap align-items-end gap-2 mb-4 desktop-filter-row">
                      <div>
                        <label>From</label>
                        <input
                          type="date"
                          className="form-control"
                          value={fromDate}
                          max={toDate || new Date().toISOString().split("T")[0]}
                          onKeyDown={(e) => e.preventDefault()}
                          onChange={(e) => setFromDate(e.target.value)}
                        />
                      </div>
                      <div>
                        <label>To</label>
                        <input
                          type="date"
                          className="form-control"
                          value={toDate}
                          min={fromDate || undefined}
                          max={new Date().toISOString().split("T")[0]}
                          onKeyDown={(e) => e.preventDefault()}
                          onChange={(e) => setToDate(e.target.value)}
                        />
                      </div>
                      <div>
                        <label>Search</label>
                        <input
                          type="search"
                          className="form-control"
                          placeholder="Search..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <div>
                        <button
                          type="button"
                          className="btn btn-primary waves-effect waves-light"
                          onClick={handleSearch}
                        >
                          Search
                        </button>
                      </div>
                      <div>
                        <button
                          type="button"
                          className="btn btn-primary waves-effect waves-light"
                          onClick={clearFilters}
                          disabled={!fromDate && !toDate && !searchTerm}
                        >
                          Clear
                        </button>
                      </div>
                      <div className="ms-auto">
                        <ExportButton onClick={handleExport} />
                      </div>
                    </div>

                    <MobileFilter
                      title="History"
                      onExport={handleExport}
                      exportDisabled={!filteredData || filteredData.length === 0}
                    >
                      <div>
                        <label>From</label>
                        <input
                          type="date"
                          className="form-control"
                          value={fromDate}
                          max={
                            toDate
                              ? toDate > new Date().toISOString().split("T")[0]
                                ? new Date().toISOString().split("T")[0]
                                : toDate
                              : new Date().toISOString().split("T")[0]
                          }
                          onKeyDown={(e) => e.preventDefault()}
                          onChange={(e) => setFromDate(e.target.value)}
                        />
                      </div>
                      <div>
                        <label>To</label>
                        <input
                          type="date"
                          className="form-control"
                          value={toDate}
                          min={fromDate || undefined}
                          max={new Date().toISOString().split("T")[0]}
                          onKeyDown={(e) => e.preventDefault()}
                          onChange={(e) => setToDate(e.target.value)}
                        />
                      </div>
                      <div>
                        <label>Search</label>
                        <input
                          type="search"
                          className="form-control"
                          placeholder="Search..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <div className="d-flex gap-2 mt-2">
                        <button
                          type="button"
                          className="btn btn-primary waves-effect waves-light flex-grow-1"
                          onClick={handleSearch}
                          data-bs-dismiss="offcanvas"
                          data-bs-target="#filterOffcanvasMobile"
                        >
                          Search
                        </button>
                        <button
                          type="button"
                          className="btn btn-light waves-effect waves-light border flex-grow-1"
                          onClick={clearFilters}
                          disabled={!fromDate && !toDate && !searchTerm}
                          data-bs-dismiss="offcanvas"
                          data-bs-target="#filterOffcanvasMobile"
                        >
                          Clear
                        </button>
                      </div>
                    </MobileFilter>

                    <CommonTable
                      columns={[
                        {
                          header: "Date",
                          render: (row) => (row.ondate ? formatDate(row.ondate) : "-"),
                        },
                        {
                          header: "Invoice ID",
                          render: (row) => (
                            <span className="fw-semibold font-monospace">
                              {row.invoiceid || row.orderno || "-"}
                            </span>
                          ),
                        },
                        {
                          header: "Amount",
                          render: (row) => (
                            <span className="fw-bold">
                              {row.amount != null
                                ? `$${Number(row.amount).toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 6,
                                  })}`
                                : "-"}
                            </span>
                          ),
                        },
                        {
                          header: "Coin",
                          render: (row) => (
                            <span className="notranslate fw-medium" translate="no">
                              {row.coinname || "-"}
                            </span>
                          ),
                        },
                        {
                          header: "Network",
                          render: (row) => (
                            <span className="badge bg-light text-dark border font-monospace">
                              {row.networkname || "-"}
                            </span>
                          ),
                        },
                        {
                          header: "Transaction Hash",
                          render: (row, idx) => {
                            if (!row.tranhash) return <span className="text-muted">-</span>;
                            const txUrl = getExplorerTxUrl(row.networkname, row.tranhash);
                            const isCopied = copiedKey === `tx_table_${row.id || idx}`;
                            return (
                              <div className="d-flex align-items-center gap-2">
                                <span title={row.tranhash} className="font-monospace">
                                  {shortAddress(row.tranhash)}
                                </span>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link text-decoration-none p-0 border-0"
                                  onClick={() =>
                                    handleCopy(
                                      row.tranhash,
                                      `tx_table_${row.id || idx}`,
                                      "Transaction Hash"
                                    )
                                  }
                                  title="Copy Transaction Hash"
                                  style={{
                                    cursor: "pointer",
                                    color: isCopied ? "#34c38f" : "#74788d",
                                    lineHeight: 1,
                                  }}
                                >
                                  <i
                                    className={
                                      isCopied ? "bx bx-check fs-6 text-success" : "bx bx-copy fs-6"
                                    }
                                  />
                                </button>
                                {txUrl && (
                                  <a
                                    href={txUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary text-decoration-none"
                                    title={`View on ${getExplorerName(row.networkname, row.to)}`}
                                    style={{ lineHeight: 1 }}
                                  >
                                    <i className="bx bx-link-external fs-6" />
                                  </a>
                                )}
                              </div>
                            );
                          },
                        },
                        {
                          header: "Status",
                          render: (row) => renderStatusBadge(row.istatus || row.status),
                        },
                        {
                          header: "Action",
                          render: (row) => (
                            <button
                              type="button"
                              className="btn btn-sm btn-soft-primary d-inline-flex align-items-center gap-1 px-2 py-1"
                              onClick={() => setSelectedDeposit(row)}
                              title="View Deposit Details"
                            >
                              <i className="bx bx-show fs-6" />
                              <span>Details</span>
                            </button>
                          ),
                        },
                      ]}
                      data={filteredData}
                      loading={loading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {/* Crypto Deposit Details Modal */}
      {selectedDeposit && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.55)", zIndex: 1050 }}
            onClick={() => setSelectedDeposit(null)}
          />

          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            style={{ zIndex: 1055 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setSelectedDeposit(null);
            }}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxWidth: "700px" }}>
              <div
                className="modal-content border-0 shadow-lg"
                style={{ borderRadius: "16px", overflow: "hidden" }}
              >
                {/* Modal Header */}
                <div className="modal-header bg-light py-3 px-4 border-bottom">
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="d-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary rounded-circle"
                      style={{ width: "38px", height: "38px" }}
                    >
                      <i className="bx bx-wallet-alt fs-4" />
                    </div>
                    <div>
                      <h5 className="modal-title fw-bold text-dark mb-0">Crypto Deposit Details</h5>
                      <small className="text-muted">
                        Invoice ID:{" "}
                        <span className="font-monospace fw-semibold text-dark">
                          {selectedDeposit.invoiceid || selectedDeposit.orderno || "-"}
                        </span>
                      </small>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSelectedDeposit(null)}
                    aria-label="Close"
                  />
                </div>

                {/* Modal Body */}
                <div className="modal-body p-4" style={{ maxHeight: "75vh", overflowY: "auto" }}>
                  {/* Amount & Status Top Banner */}
                  <div className="p-3 mb-4 rounded-3 bg-light border d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div>
                      <div className="text-muted font-size-12 fw-medium text-uppercase">
                        Deposit Amount
                      </div>
                      <div className="d-flex align-items-baseline gap-2 mt-1">
                        <span className="fs-3 fw-bold text-primary">
                          $
                          {selectedDeposit.amount != null
                            ? Number(selectedDeposit.amount).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 6,
                              })
                            : "0.00"}
                        </span>
                        <span className="badge bg-primary bg-opacity-10 text-primary font-size-13 fw-semibold">
                          {selectedDeposit.coinname || "USDT"}
                        </span>
                        <span className="badge bg-secondary bg-opacity-10 text-secondary font-size-12 font-monospace">
                          {selectedDeposit.networkname || "-"}
                        </span>
                      </div>
                    </div>

                    <div className="text-end">
                      <div className="text-muted font-size-12 fw-medium text-uppercase mb-1">
                        Status
                      </div>
                      <div>
                        {renderStatusBadge(selectedDeposit.istatus || selectedDeposit.status)}
                      </div>
                      <div className="text-muted font-size-11 mt-1">
                        <i className="bx bx-calendar me-1" />
                        {selectedDeposit.ondate ? formatDate(selectedDeposit.ondate) : "-"}
                      </div>
                    </div>
                  </div>

                  {/* Section 1: Blockchain & Transfer Information */}
                  <div className="mb-4">
                    <h6 className="text-uppercase font-size-12 text-primary fw-bold mb-3 d-flex align-items-center gap-1">
                      <i className="bx bx-transfer-alt fs-5" />
                      Blockchain Details
                    </h6>

                    {/* Deposit Address (To) */}
                    <div className="mb-3">
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <label className="form-label text-muted font-size-12 mb-0 fw-semibold">
                          Deposit Address (To)
                        </label>
                        {selectedDeposit.to && (
                          <span className="text-muted font-size-11">
                            Scanner:{" "}
                            <span className="fw-semibold text-dark">
                              {getExplorerName(selectedDeposit.networkname, selectedDeposit.to)}
                            </span>
                          </span>
                        )}
                      </div>

                      <div className="p-2 px-3 rounded bg-light border d-flex align-items-center justify-content-between gap-2 flex-wrap">
                        <span className="font-monospace text-dark text-break font-size-13 fw-medium">
                          {selectedDeposit.to || "-"}
                        </span>

                        {selectedDeposit.to && (
                          <div className="d-flex align-items-center gap-2 ms-auto">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1 py-1 px-2"
                              onClick={() =>
                                handleCopy(selectedDeposit.to, "modal_to", "Deposit Address")
                              }
                              title="Copy Address"
                            >
                              <i
                                className={
                                  copiedKey === "modal_to"
                                    ? "bx bx-check text-success"
                                    : "bx bx-copy"
                                }
                              />
                              <span>{copiedKey === "modal_to" ? "Copied" : "Copy"}</span>
                            </button>

                            {getExplorerAddressUrl(
                              selectedDeposit.networkname,
                              selectedDeposit.to
                            ) && (
                              <a
                                href={getExplorerAddressUrl(
                                  selectedDeposit.networkname,
                                  selectedDeposit.to
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-soft-primary d-inline-flex align-items-center gap-1 py-1 px-2"
                                title="Open address on blockchain scanner"
                              >
                                <i className="bx bx-link-external" />
                                <span>View on Scan</span>
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Transaction Hash */}
                    <div className="mb-3">
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <label className="form-label text-muted font-size-12 mb-0 fw-semibold">
                          Transaction Hash (TxID)
                        </label>
                      </div>

                      <div className="p-2 px-3 rounded bg-light border d-flex align-items-center justify-content-between gap-2 flex-wrap">
                        {selectedDeposit.tranhash ? (
                          <>
                            <span className="font-monospace text-dark text-break font-size-13 fw-medium">
                              {selectedDeposit.tranhash}
                            </span>
                            <div className="d-flex align-items-center gap-2 ms-auto">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1 py-1 px-2"
                                onClick={() =>
                                  handleCopy(
                                    selectedDeposit.tranhash,
                                    "modal_tx",
                                    "Transaction Hash"
                                  )
                                }
                                title="Copy Transaction Hash"
                              >
                                <i
                                  className={
                                    copiedKey === "modal_tx"
                                      ? "bx bx-check text-success"
                                      : "bx bx-copy"
                                  }
                                />
                                <span>{copiedKey === "modal_tx" ? "Copied" : "Copy"}</span>
                              </button>

                              {getExplorerTxUrl(
                                selectedDeposit.networkname,
                                selectedDeposit.tranhash
                              ) && (
                                <a
                                  href={getExplorerTxUrl(
                                    selectedDeposit.networkname,
                                    selectedDeposit.tranhash
                                  )}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-soft-primary d-inline-flex align-items-center gap-1 py-1 px-2"
                                  title="Open transaction on blockchain scanner"
                                >
                                  <i className="bx bx-link-external" />
                                  <span>View on Scan</span>
                                </a>
                              )}
                            </div>
                          </>
                        ) : (
                          <span className="text-muted font-size-13 fst-italic">
                            Not available yet (Transaction pending or incomplete)
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Sender Address (From) if present */}
                    {selectedDeposit.from && (
                      <div className="mb-3">
                        <label className="form-label text-muted font-size-12 mb-1 fw-semibold">
                          Sender Address (From)
                        </label>
                        <div className="p-2 px-3 rounded bg-light border d-flex align-items-center justify-content-between gap-2 flex-wrap">
                          <span className="font-monospace text-dark text-break font-size-13">
                            {selectedDeposit.from}
                          </span>
                          <div className="d-flex align-items-center gap-2 ms-auto">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1 py-1 px-2"
                              onClick={() =>
                                handleCopy(selectedDeposit.from, "modal_from", "Sender Address")
                              }
                            >
                              <i
                                className={
                                  copiedKey === "modal_from"
                                    ? "bx bx-check text-success"
                                    : "bx bx-copy"
                                }
                              />
                              <span>{copiedKey === "modal_from" ? "Copied" : "Copy"}</span>
                            </button>

                            {getExplorerAddressUrl(
                              selectedDeposit.networkname,
                              selectedDeposit.from
                            ) && (
                              <a
                                href={getExplorerAddressUrl(
                                  selectedDeposit.networkname,
                                  selectedDeposit.from
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-soft-primary d-inline-flex align-items-center gap-1 py-1 px-2"
                              >
                                <i className="bx bx-link-external" />
                                <span>View on Scan</span>
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Section 2: Order & Reference Details */}
                  <div className="mb-4">
                    <h6 className="text-uppercase font-size-12 text-primary fw-bold mb-3 d-flex align-items-center gap-1">
                      <i className="bx bx-receipt fs-5" />
                      Order & Reference Details
                    </h6>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="border rounded p-2 px-3 h-100">
                          <div className="text-muted font-size-11">Invoice ID</div>
                          <div className="d-flex align-items-center justify-content-between mt-1">
                            <span className="fw-semibold font-monospace text-dark">
                              {selectedDeposit.invoiceid || "-"}
                            </span>
                            {selectedDeposit.invoiceid && (
                              <button
                                type="button"
                                className="btn btn-sm btn-link p-0 text-muted"
                                onClick={() =>
                                  handleCopy(selectedDeposit.invoiceid, "modal_inv", "Invoice ID")
                                }
                                title="Copy Invoice ID"
                              >
                                <i
                                  className={
                                    copiedKey === "modal_inv"
                                      ? "bx bx-check text-success"
                                      : "bx bx-copy"
                                  }
                                />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="border rounded p-2 px-3 h-100">
                          <div className="text-muted font-size-11">Order No.</div>
                          <div className="d-flex align-items-center justify-content-between mt-1">
                            <span
                              className="fw-semibold font-monospace text-dark text-truncate"
                              style={{ maxWidth: "220px" }}
                              title={selectedDeposit.orderno}
                            >
                              {selectedDeposit.orderno || "-"}
                            </span>
                            {selectedDeposit.orderno && (
                              <button
                                type="button"
                                className="btn btn-sm btn-link p-0 text-muted"
                                onClick={() =>
                                  handleCopy(selectedDeposit.orderno, "modal_order", "Order No")
                                }
                                title="Copy Order No"
                              >
                                <i
                                  className={
                                    copiedKey === "modal_order"
                                      ? "bx bx-check text-success"
                                      : "bx bx-copy"
                                  }
                                />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="border rounded p-2 px-3 h-100">
                          <div className="text-muted font-size-11">Coin & Network</div>
                          <div className="fw-semibold text-dark mt-1">
                            {selectedDeposit.coinname || "-"} (
                            {selectedDeposit.networkname || "-"})
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="border rounded p-2 px-3 h-100">
                          <div className="text-muted font-size-11">Gateway Message</div>
                          <div className="fw-semibold text-dark mt-1">
                            {selectedDeposit.msg || "-"}
                            {selectedDeposit.msgcode ? ` (${selectedDeposit.msgcode})` : ""}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: User Details (from API response) */}
                  {(selectedDeposit.loginid ||
                    selectedDeposit.email ||
                    selectedDeposit.fname ||
                    selectedDeposit.mobile) && (
                    <div className="mb-3">
                      <h6 className="text-uppercase font-size-12 text-primary fw-bold mb-3 d-flex align-items-center gap-1">
                        <i className="bx bx-user-circle fs-5" />
                        Account Information
                      </h6>
                      <div className="row g-3">
                        {selectedDeposit.loginid && (
                          <div className="col-sm-6 col-md-3">
                            <div className="border rounded p-2 px-3 h-100">
                              <div className="text-muted font-size-11">Login ID</div>
                              <div className="fw-bold text-dark font-monospace mt-1">
                                {selectedDeposit.loginid}
                              </div>
                            </div>
                          </div>
                        )}

                        {(selectedDeposit.fname || selectedDeposit.lname) && (
                          <div className="col-sm-6 col-md-3">
                            <div className="border rounded p-2 px-3 h-100">
                              <div className="text-muted font-size-11">Name</div>
                              <div className="fw-semibold text-dark mt-1 text-truncate">
                                {`${selectedDeposit.fname || ""} ${
                                  selectedDeposit.lname || ""
                                }`.trim()}
                              </div>
                            </div>
                          </div>
                        )}

                        {selectedDeposit.email && (
                          <div className="col-sm-6 col-md-3">
                            <div className="border rounded p-2 px-3 h-100">
                              <div className="text-muted font-size-11">Email</div>
                              <div
                                className="fw-medium text-dark font-size-12 mt-1 text-truncate"
                                title={selectedDeposit.email}
                              >
                                {selectedDeposit.email}
                              </div>
                            </div>
                          </div>
                        )}

                        {selectedDeposit.mobile && (
                          <div className="col-sm-6 col-md-3">
                            <div className="border rounded p-2 px-3 h-100">
                              <div className="text-muted font-size-11">Mobile</div>
                              <div className="fw-medium text-dark font-size-12 mt-1">
                                {selectedDeposit.mobile}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Rejection / Note reason if any */}
                  {selectedDeposit.reject_reason && (
                    <div className="alert alert-danger d-flex align-items-center gap-2 mb-0 mt-3 py-2 px-3">
                      <i className="bx bx-error-circle fs-4" />
                      <div>
                        <strong>Rejection Reason:</strong> {selectedDeposit.reject_reason}
                      </div>
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="modal-footer bg-light py-2 px-4 border-top d-flex justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    {getExplorerAddressUrl(selectedDeposit.networkname, selectedDeposit.to) && (
                      <a
                        href={getExplorerAddressUrl(
                          selectedDeposit.networkname,
                          selectedDeposit.to
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                      >
                        <i className="bx bx-link-external" />
                        <span>Scan Address</span>
                      </a>
                    )}
                    {selectedDeposit.tranhash &&
                      getExplorerTxUrl(
                        selectedDeposit.networkname,
                        selectedDeposit.tranhash
                      ) && (
                        <a
                          href={getExplorerTxUrl(
                            selectedDeposit.networkname,
                            selectedDeposit.tranhash
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                        >
                          <i className="bx bx-link-external" />
                          <span>Scan TxID</span>
                        </a>
                      )}
                  </div>

                  <button
                    type="button"
                    className="btn btn-secondary px-4"
                    onClick={() => setSelectedDeposit(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

